// =====================================================================
// Faz M (2026-05-06) — Action Center
// =====================================================================
// "Bugün ne yapmalıyım?" sorusunu cevaplayan tek sayfa.
// Mevcut /api/admin/insights/notifications endpoint'i 10 farklı kategoride
// alert döndürüyor (yeni sipariş, kritik stok, bekleyen onay, NPS detractor,
// terk edilmiş yüksek değerli sepet, bekleyen ödeme, süresi yaklaşan kupon,
// kupon limit doluyor, satış trend, BH entegrasyon hata).
//
// Bu sayfa onları:
//   - severity'ye göre gruplar (error / warning / info / success)
//   - kategori filter sağlar
//   - tek tıkla ilgili sayfaya götürür (link prop'u)
// =====================================================================
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, Grid, Chip, Alert, IconButton, Tooltip, Button,
  ToggleButton, ToggleButtonGroup, CircularProgress, List, ListItem,
  ListItemText, ListItemButton, Divider
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import API from "../services/api";

// Severity → renk + ikon (mail HTML'lerle aynı renk paletinde tutarlı)
const SEV_META = {
  error:   { color: "#ef4444", bg: "#fef2f2", label: "Acil",   icon: <ErrorIcon /> },
  warning: { color: "#f59e0b", bg: "#fffbeb", label: "Dikkat", icon: <WarningIcon /> },
  info:    { color: "#3b82f6", bg: "#eff6ff", label: "Bilgi",  icon: <InfoIcon /> },
  success: { color: "#10b981", bg: "#f0fdf4", label: "Başarı", icon: <CheckCircleIcon /> }
};

// Kategori (type) → daha okunur etiket
const TYPE_LABELS = {
  new_order:           "Yeni Sipariş",
  stock_alert:         "Stok",
  pending_user:        "Müşteri Onayı",
  nps_detractor:       "NPS Detractor",
  stale_cart:          "Terk Edilmiş Sepet",
  pending_payment:     "Bekleyen Ödeme",
  coupon_expiring:     "Kupon Süresi",
  coupon_hot:          "Kupon Limit",
  sales_trend_down:    "Satış Düşüşü",
  sales_trend_up:      "Satış Artışı",
  bh_integration_fail: "BH Hatası"
};

export default function ActionCenter() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");  // all | error | warning | info | success

  const load = () => {
    setLoading(true);
    API.get("/api/admin/insights/notifications")
      .then(r => { setItems(r.data.items || []); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Severity sayıları (filter butonlarındaki badge'ler için)
  const counts = useMemo(() => {
    const c = { all: items.length, error: 0, warning: 0, info: 0, success: 0 };
    for (const i of items) c[i.severity] = (c[i.severity] || 0) + 1;
    return c;
  }, [items]);

  // Filtrelenmiş + severity sıralı liste (acil önce)
  const filtered = useMemo(() => {
    const list = filter === "all" ? items : items.filter(i => i.severity === filter);
    const order = { error: 0, warning: 1, info: 2, success: 3 };
    return [...list].sort((a, b) => (order[a.severity] || 9) - (order[b.severity] || 9));
  }, [items, filter]);

  // Kategori-bazlı gruplama (type → items)
  const grouped = useMemo(() => {
    const g = {};
    for (const i of filtered) {
      const k = i.type;
      if (!g[k]) g[k] = [];
      g[k].push(i);
    }
    return g;
  }, [filtered]);

  if (loading && items.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Action Center</Typography>
          <Typography variant="body2" color="text.secondary">
            Bugün ne yapmalıyım — operasyonel uyarılar tek sayfada
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* Severity KPI kartları + filtre olarak kullanılır */}
      <Grid container spacing={2} mb={3}>
        {[
          { code: "all",     label: "Hepsi",  color: "#1565C0", count: counts.all },
          { code: "error",   label: "Acil",   color: SEV_META.error.color,   count: counts.error },
          { code: "warning", label: "Dikkat", color: SEV_META.warning.color, count: counts.warning },
          { code: "info",    label: "Bilgi",  color: SEV_META.info.color,    count: counts.info },
          { code: "success", label: "Başarı", color: SEV_META.success.color, count: counts.success }
        ].map(s => (
          <Grid item xs={6} sm={4} md={2.4} key={s.code}>
            <Card
              onClick={() => setFilter(s.code)}
              sx={{
                p: 2, borderRadius: 3, cursor: "pointer",
                border: filter === s.code ? `2px solid ${s.color}` : "2px solid transparent",
                bgcolor: filter === s.code ? `${s.color}11` : "white",
                transition: "all 0.2s"
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {s.label.toUpperCase()}
              </Typography>
              <Typography variant="h3" fontWeight={800} sx={{ color: s.color, lineHeight: 1.1 }}>
                {s.count}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Boş durum */}
      {filtered.length === 0 && (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {filter === "all"
            ? "Aktif uyarı yok — sistem temiz görünüyor 🎉"
            : `${SEV_META[filter]?.label || filter} kategorisinde uyarı yok`}
        </Alert>
      )}

      {/* Kategori-grup card'ları */}
      {Object.keys(grouped).map(type => {
        const list = grouped[type];
        const sev = list[0].severity;
        const meta = SEV_META[sev] || SEV_META.info;
        return (
          <Card key={type} sx={{ borderRadius: 3, mb: 2, overflow: "hidden", borderLeft: `4px solid ${meta.color}` }}>
            <Box sx={{
              px: 2.5, py: 1.5, bgcolor: meta.bg,
              display: "flex", alignItems: "center", gap: 1.5
            }}>
              <Box sx={{ color: meta.color }}>{meta.icon}</Box>
              <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                {TYPE_LABELS[type] || type}
              </Typography>
              <Chip
                label={list.length}
                size="small"
                sx={{ bgcolor: meta.color, color: "white", fontWeight: 700 }}
              />
            </Box>
            <List dense disablePadding>
              {list.map((item, idx) => (
                <React.Fragment key={idx}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => item.link && navigate(item.link)}
                      sx={{ py: 1.5, px: 2.5 }}
                    >
                      <ListItemText
                        primary={
                          <Typography fontWeight={600} fontSize={14}>{item.title}</Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                            {item.body}
                          </Typography>
                        }
                      />
                      {item.link && <ChevronRightIcon sx={{ color: "#94a3b8" }} />}
                    </ListItemButton>
                  </ListItem>
                  {idx < list.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        );
      })}
    </Box>
  );
}
